import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useClerkAuth';
import { Article } from '@/types/article';
import { fetchArticles, updateArticle } from '@/services/articles';
import ArticleEditor from '@/components/ArticleEditor';
import { Loader2 } from 'lucide-react';

const EditArticle = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadArticle = async () => {
      if (!id) {
        navigate('/dashboard');
        return;
      }

      try {
        const articles = await fetchArticles();
        const foundArticle = articles.find(a => a.id === id);

        if (!foundArticle) {
          toast({
            title: t('editArticle.articleNotFound'),
            description: t('editArticle.articleNotFoundDesc'),
            variant: 'destructive',
          });
          navigate('/dashboard');
          return;
        }

        setArticle(foundArticle);
      } catch (error) {
        console.error('Error loading article:', error);
        toast({
          title: t('editArticle.loadError'),
          description: t('editArticle.loadErrorDesc'),
          variant: 'destructive',
        });
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [id, navigate, toast, t]);

  const handleSave = async (
    updatedArticleData: Omit<Article, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    if (!article || saving || !user?.id) return;

    setSaving(true);
    try {
      const updatedArticle = await updateArticle(
        user.id,
        article.id,
        updatedArticleData
      );
      setArticle(updatedArticle);

      toast({
        title: t('editArticle.articleUpdated'),
        description: `Article "${updatedArticle.title}" has been updated successfully.`,
      });
    } catch (error) {
      console.error('Error updating article:', error);
      toast({
        title: t('editArticle.updateFailed'),
        description: t('editArticle.updateFailedDesc'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading article...</span>
        </div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <ArticleEditor
      article={article}
      onSave={handleSave}
      onClose={handleClose}
    />
  );
};

export default EditArticle;
